// Session 78n8.3, Issue #413.
//
// WHAT THIS IS, in plain words: the "copy" button. Somebody looking at a
// tracking number or a PO number almost always wants it somewhere else -- into
// the carrier's website, into an email to the vendor, into a text message. This
// puts it on their clipboard.
//
// WHY IT IS A SHARED FILE RATHER THAN A LOCAL HELPER. The exact fallback below
// was already written once, privately, inside TrackingRow.js, and #413 needs the
// same thing on the My Requests card. Copying it would have made two, and the
// third would have been written the next time somebody displays a reference
// number. This is the duplication Engineering Standards rule 14 names: the
// repetition is in the SIDE EFFECT, not in the UI shape.
//
// WHY THE FALLBACK EXISTS AT ALL, since it looks like superstition. The modern
// clipboard API is gated on a SECURE CONTEXT -- https, or localhost. It is
// absent over plain http and it rejects when the document is not focused. Stede
// is used on phones in a shop, over whatever the site wifi is doing, and a copy
// button that silently does nothing is worse than no copy button: the number is
// right there, so the person believes they copied it and pastes the previous
// thing on their clipboard. The textarea-and-execCommand path is deprecated and
// works everywhere, which is exactly the trade a fallback should make.

// Resolves true when the text is on the clipboard, false when it could not be.
// NEVER REJECTS, deliberately -- a caller showing a tick is not doing work that
// deserves a try/catch, and a rejected promise here would surface as an unhandled
// rejection in the console of a page that is otherwise fine.
export function copyText(value) {
  const text = value == null ? '' : String(value);
  if (!text) return Promise.resolve(false);

  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => legacyCopy(text));
  }
  return Promise.resolve(legacyCopy(text));
}

function legacyCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    // Off-screen rather than hidden: a display:none or visibility:hidden element
    // cannot be selected, so the copy would quietly do nothing.
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    // Clipboard genuinely unavailable. The caller shows no tick and the number
    // stays on screen to be copied by hand, which is the honest outcome.
    return false;
  }
}
